import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, Renderer2, ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LayoutBaseRenderComponent } from 'render/render-shared/layout-base-render/layout-base-render.component';
import { RenderSeoService } from 'render/services/render-seo.service';
import { fromEvent, Subject } from 'rxjs';
import { map, filter, tap, takeUntil } from 'rxjs/operators';

import { LayoutVerticalSliderSettings } from './layout-vertical-slider.models';


@Component({
  selector: 'layout-vertical-slider-render',
  templateUrl: './layout-vertical-slider-render.component.html',
  styleUrls: ['./layout-vertical-slider-render.component.scss']
})
export class LayoutVerticalSliderRenderComponent
  extends LayoutBaseRenderComponent<LayoutVerticalSliderSettings>
  implements AfterViewInit, OnDestroy {

  public stickyFooter: boolean = false;
  public bullets: boolean = false;
  public bulletsArray: boolean[] = [];

  @ViewChild('verticalSlider') private readonly slider!: ElementRef;

  private lastScroll = this.getTime();
  private readonly ANIMATION_THRESHOLD = 1000;
  private readonly TOUCH_THRESHOLD = 100;
  private currentSlide = 1;
  private magnetScroll!: HTMLElement;
  private magnetFooterScroll!: HTMLElement;
  private numberOfSlides = 0;
  private touchStart = 0;
  private parsed = true;
  private onRelease?: () => void;
  private scrollings: number[] = [];
  private lastMove: number = this.getTime();
  private footerAsBullet: boolean = false;

  constructor(
    protected readonly route: ActivatedRoute,
    protected readonly seoService: RenderSeoService,
    @Inject('Window') private readonly window: Window,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly renderer: Renderer2,
    private readonly cd: ChangeDetectorRef
  ) {
    super(route, seoService);
  }

  public ngAfterViewInit() {
    this.renderer.setStyle(this.document.body, 'touch-action', 'pan-y'); // Disable zooming
    this.backgroundColor = this.layout?.settings?.styles?.bgColor;
    this.magnetScroll = this.slider.nativeElement.getElementsByClassName(
      'slot-vertical-sliders'
    )[0];
    this.magnetFooterScroll = this.slider.nativeElement.getElementsByClassName(
      'slot-footer'
    )[0];
    if (
      this.magnetScroll.querySelector('render-slice-slot') === null
      && this.magnetScroll.querySelector('slice-slider-render') === null
    ) {
      this.onMutation()
        .pipe(
          takeUntil(this.clearSubscriptions$)
        )
        .subscribe(() => {
          this.initSlides();
        });
    } else {
      this.initSlides();
    }
  }

  public goToSlide(slide: number) {
    const isFooterAsBullet = this.hasFooter() && this.footerAsBullet;
    if (slide > 0 && (slide <= this.numberOfSlides || isFooterAsBullet)) {
      this.lastMove = this.getTime();
      this.currentSlide = slide;
      this.updateScroll();
    }
  }

  public isCurrentSlide(slide: number): boolean {
    return slide === this.currentSlide;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  protected onInitHook(): void {
    this.stickyFooter = this.layout?.settings?.general?.stickyFooter as boolean ?? false;
    this.bullets = this.layout?.settings?.general?.bullets as boolean ?? false;
    this.footerAsBullet = this.layout?.settings?.general?.footerAsBullet as boolean ?? false;
  }

  private initSlides() {
    this.clearEmptySlides();
    this.numberOfSlides = this.magnetScroll.children.length;
    let numberOfBullets = this.numberOfSlides;
    if (this.bullets && this.hasFooter() && this.footerAsBullet) {
      numberOfBullets++;
    }
    this.bulletsArray = new Array(numberOfBullets);

    this.updateScroll();
    this.updateHeight();
    this.magnetScroll.style.height = `calc(100% * ${this.numberOfSlides})`;
    // TODO Move this to slot settings
    Array.from(this.magnetScroll.children).forEach((i) => {
      (i as HTMLElement).style.height = `calc(100% / ${this.numberOfSlides})`;
    });
    this.initTouch();
    this.initWheel();
    this.initKeyboard();
    this.window.addEventListener('resize', () => this.onResize());
    this.cd.markForCheck();
  }

  private initTouch() {
    fromEvent<TouchEvent>(this.slider.nativeElement, 'touchstart', { passive: true }).pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe((e: TouchEvent) => {
      this.touchStart = e.touches[0].clientY;
      this.parsed = false;
    });

    const move$ = fromEvent<TouchEvent>(this.slider.nativeElement, 'touchmove', { passive: true })
      .pipe(
        filter(() => !this.parsed),
        map((e: TouchEvent) => e.touches[0].clientY),
      );
    const moveOnSlides$ = move$.pipe(
      filter(() => this.currentSlide <= this.numberOfSlides),
    );
    const moveOnFooter$ = move$.pipe(
      filter(() => this.currentSlide > this.numberOfSlides),
    );

    moveOnSlides$
      .pipe(
        filter((touchMove) => Math.abs(touchMove - this.touchStart) > this.TOUCH_THRESHOLD),
        takeUntil(this.clearSubscriptions$)
      )
      .subscribe((touchMove) => {
        if (touchMove - this.touchStart < 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
        this.parsed = true;
      });
    moveOnFooter$
      .pipe(
        filter((touchMove) => {
          const scroll = this.getPercentageOfScroll(this.magnetFooterScroll);
          return scroll === 0 && touchMove - this.touchStart > 0;
        }),
        takeUntil(this.clearSubscriptions$)
      )
      .subscribe(() => {
        this.prevSlide();
        this.parsed = true;
      });

    fromEvent(this.slider.nativeElement, 'touchend').pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe(() => {
      this.parsed = true;
      if (this.onRelease) {
        this.onRelease();
      }
    });
  }

  private initWheel() {
    const acceptedMovement$ = fromEvent<WheelEvent>(this.slider.nativeElement, 'wheel', { passive: true })
      .pipe(
        filter((e: WheelEvent) => Math.abs(e.deltaX) < Math.abs(e.deltaY)),
        map((e: WheelEvent) => e.deltaY),
        tap((deltaY: number) => this.updateHistory(this.getTime(), deltaY)),
        filter(() => this.getTime() - this.lastMove > this.ANIMATION_THRESHOLD),
      );
    acceptedMovement$
      .pipe(
        filter(() => this.isAccelerating()),
        takeUntil(this.clearSubscriptions$)
      )
      .subscribe((deltaY: number) => {
        if (deltaY > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      });
    acceptedMovement$
      .pipe(
        filter(() => !this.isAccelerating()),
        takeUntil(this.clearSubscriptions$)
      )
      .subscribe(() => {
        if (this.onRelease) {
          this.onRelease();
        }
      });
  }

  private initKeyboard() {
    const codeMap: Record<string, () => void> = {
      ArrowUp: () => this.prevSlide(),
      ArrowDown: () => this.nextSlide(),
      PageUp: () => this.prevSlide(),
      PageDown: () => this.nextSlide()
    };
    fromEvent<KeyboardEvent>(this.document, 'keydown')
      .pipe(
        map((e: KeyboardEvent) => e.key),
        takeUntil(this.clearSubscriptions$)
      )
      .subscribe((code: string) => {
        if (codeMap[code]) {
          codeMap[code]();
        }
      });
  }

  private onMutation() {
    const mutated: Subject<void> = new Subject();
    if ('MutationObserver' in this.window && this.magnetScroll instanceof Node) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const observer = new (this.window as any).MutationObserver(() => {
        observer.disconnect();
        mutated.next();
      });
      observer.observe(this.magnetScroll, { subtree: true, childList: true });
    }
    return mutated;
  }

  private clearEmptySlides() {
    const slides = this.magnetScroll.children;
    Array.from(slides).forEach((slide) => {
      if (slide.querySelector('render-piece') === null && slide.querySelector('slice-slider-render') === null) {
        slide.remove();
      }
    });
  }

  // Source: https://github.com/alvarotrigo/fullPage.js/blob/650d52f83f3d702a8ff52673fd05a1a28cea497f/src/fullpage.js#L1561
  private isAccelerating() {
    const averageEnd = this.getAverage(this.scrollings, 10);
    const averageMiddle = this.getAverage(this.scrollings, 70);
    return averageEnd >= averageMiddle;
  }

  private updateHistory(time: number, deltaY: number) {
    const timeDiff = time - this.lastScroll;
    this.lastScroll = time;

    if (timeDiff > 200) {
      this.scrollings = [];
    } else {
      if (this.scrollings.length >= 70) {
        this.scrollings.shift();
      }

      this.scrollings.push(Math.abs(deltaY));
    }
  }

  private nextSlide() {
    if (this.currentSlide < this.numberOfSlides || (this.currentSlide === this.numberOfSlides && this.hasFooter())) {
      this.lastMove = this.getTime();
      this.currentSlide += 1;
      this.updateScroll();
      this.cd.markForCheck();
    }
  }

  private prevSlide() {
    if (this.currentSlide > 1) {
      if (this.currentSlide > this.numberOfSlides && this.getPercentageOfScroll(this.magnetFooterScroll) !== 0) {
        return;
      }
      this.lastMove = this.getTime();
      this.currentSlide -= 1;
      this.updateScroll();
      this.cd.markForCheck();
    }
  }

  private hasFooter() {
    return this.magnetFooterScroll.children.length !== 0 && !this.stickyFooter;
  }

  private updateHeight() {
    this.slider.nativeElement.style.height = `${window.innerHeight}px`;
  }

  private updateScroll() {
    this.moveY(this.magnetScroll, this.getAccumulatedHeight(this.currentSlide) * -1);
    this.moveY(this.magnetFooterScroll, this.getAccumulatedOffset(this.currentSlide));
    this.updateOverscroll();
  }

  private moveY(el: HTMLElement, movement: number) {
    el.style.transform = `translate3d(0, ${movement}%, 0`;
  }

  private getPercentageOfScroll(element: HTMLElement) {
    const relativeScrollHeight = element.scrollHeight - element.clientHeight;
    if (relativeScrollHeight === 0) {
      return 0;
    }
    return ((element.scrollTop / relativeScrollHeight) * 100);
  }

  private getTime(): number {
    return new Date().getTime();
  }

  private getAccumulatedHeight(currentSlide: number) {
    return (currentSlide - 1) * (100 / this.numberOfSlides);
  }

  private getAccumulatedOffset(currentSlide: number) {
    return (this.numberOfSlides - currentSlide + 1) * 100;
  }

  private getAverage(elements: number[], quantity: number) {
    const q = Math.min(elements.length, quantity);
    const sum = elements
      .slice(elements.length - q)
      .reduce((acc, i) => acc + i, 0);
    return sum / q;
  }

  private updateOverscroll() {
    if (this.currentSlide === 1) {
      this.renderer.setStyle(this.document.body, 'overscroll-behavior-y', 'unset');
    } else {
      if (this.document.body.style.getPropertyValue('overscroll-behavior-y') === 'unset') {
        this.onRelease = () => {
          this.renderer.setStyle(this.document.body, 'overscroll-behavior-y', 'none');
          delete this.onRelease;
        };
      }
    }
  }

  private onResize() {
    this.updateHeight();
  }

}
